import { createActionHandler, createReducer } from '@pressreader/appstore';

import type { ArticleSocialMetadata } from '../../types';
import { articleSocialMetaUpdated } from '../actions';

interface State {
    /**
     * Articles state.
     *
     * For now it's a map of primaryArticleId to ArticleState.
     */
    articlesSocialMeta: Record<string, ArticleSocialMetadata>;
}

const initialState: State = {
    articlesSocialMeta: {},
};

function updateArticleSocialMetadata<C extends ArticleSocialMetadata | undefined, U extends ArticleSocialMetadata>(
    current: C,
    update: U,
): C | (C & U) {
    const metadataPatch = {} as Partial<U>;
    let isMetaChanged = false;

    if (Number.isInteger(update.commentsCount) && update.commentsCount !== current?.commentsCount) {
        metadataPatch.commentsCount = update.commentsCount;
        isMetaChanged = true;
    }
    if (Number.isInteger(update.upVotes) && update.upVotes !== current?.upVotes) {
        metadataPatch.upVotes = update.upVotes;
        isMetaChanged = true;
    }
    if (Number.isInteger(update.downVotes) && update.downVotes !== current?.downVotes) {
        metadataPatch.downVotes = update.downVotes;
        isMetaChanged = true;
    }

    if (!isMetaChanged) {
        return current;
    }
    return {
        ...current,
        ...metadataPatch,
    };
}

const reducer = createReducer(
    initialState,
    createActionHandler(articleSocialMetaUpdated, (state, action): State => {
        const { articleSocialMetaUpdates } = action.payload;
        const { articlesSocialMeta: articlesSocialMetaCurrent } = state;
        let metaChanged = false;
        const articlesSocialMetaUpdatedState = articleSocialMetaUpdates.reduce(
            (acc, { articleId, socialMetadataUpdate }) => {
                const currentSocialMeta = acc[articleId];
                const articleSocialMeta: ArticleSocialMetadata | undefined = updateArticleSocialMetadata(currentSocialMeta, socialMetadataUpdate);
                if (articleSocialMeta !== currentSocialMeta) {
                    metaChanged = true;
                    acc[articleId] = articleSocialMeta;
                }
                return acc;
            },
            {
                ...articlesSocialMetaCurrent,
            },
        );

        if (!metaChanged) {
            return state;
        }
        return {
            ...state,
            articlesSocialMeta: articlesSocialMetaUpdatedState,
        };
    }),
);

export { State };
export { reducer };
